// EventBus
import maEventbus from '@/components/eventbus'

// components
import maInputText from '@/components/input/modules/text'
import maInputTextNumbers from '@/components/input/modules/text-numbers'
import maInputTextArea from '@/components/input/modules/text-area'
import maInputSelect from '@/components/input/modules/select'
import maInputRadio from '@/components/input/modules/radio'
import maInputCheckbox from '@/components/input/modules/checkbox'
import maInputTextmap from '@/components/input/modules/textmap'
import maInputTextmapline from '@/components/input/modules/textmapline'
import maInputImgradio from '@/components/input/modules/imgRadio'
import maInputSelectMap from '@/components/input/modules/selectmap'
import maInputBtn from '@/components/input/modules/btn'
import maCounter from '@/components/counter'
import maIframeUptec from '@/components/furniture'
// vuex
import { mapState, mapMutations } from 'vuex'

// types
import {
  WARRANTY_MODULE_NAME,
  WARRANTY_UPDATE_PIECES_SURFACE
} from '@/store/types'

// import event Bus
import maEventBus from '@/components/eventbus/index.js'

// constants
import {
  DAMAGE_SURFACE,
  PIECE,
  ROOM_SURFACE,
  TEXT_NUMBERS,
  UPDATE_PIECES_IDS,
  UPDATE_PIECES_SURFACE
} from '@/constants'

/**
 * @module
 * @name Form
 * @description Dynamic form
 */
export default {
  name: 'Form',
  data: function() {
    return {
      data: undefined
    }
  },
  /**
   * @property {String} type CSS style
   * @property {Object} formData Form components will be rendered according
   *                             to this object.
   */
  props: {
    type: {
      type: String,
      required: true
    },
    formData: {
      type: Object,
      required: false
    }
  },
  components: {
    maInputText,
    maInputTextNumbers,
    maInputTextArea,
    maInputSelect,
    maInputRadio,
    maInputCheckbox,
    maInputTextmap,
    maInputTextmapline,
    maInputImgradio,
    maInputSelectMap,
    maInputBtn,
    maIframeUptec,
    maCounter
  },

  /**
   * @property {Boolean} agentView From state, it shows different input components
   *                               depending on if we're working with agentView
   *                               or not.
   * @property {Object} stateWarrantyCoordinates Retrieved from state, set in
   *                                             sinister address for inputs that
   *                                             need it.
   */
  computed: {
    ...mapState({
      agentView: state => state.agentView
    }),
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantyCoordinates: 'coordinates',
      stateWarrantySinisterSummary: 'sinisterSummary'
    }),
    ...mapState({
      piecesMaxIdList: state => {
        const sinisterSummary = state.warranty.sinisterSummary
        if (sinisterSummary.length === 0) {
          return []
        }

        return sinisterSummary[0].pieces_max_id
      }
    }),
    ...mapState({
      piecesSurfaceList: state => state.warranty.piecesSurface || []
    }),
    ...mapState({
      lastPieceSelected: state => state.warranty.lastPieceSelected
    }),
    ...mapState({
      sinisterPiecesList: state => {
        if (
          state.warranty.sinisterSummary &&
          state.warranty.sinisterSummary[0]
        ) {
          return state.warranty.sinisterSummary[0].items || []
        }

        return []
      }
    })
  },

  methods: {
    ...mapMutations(WARRANTY_MODULE_NAME, {
      actUdatePiecesSurface: WARRANTY_UPDATE_PIECES_SURFACE
    }),
    /**
     * @function
     * @name handleChangeView
     * @description Emits and event to resize accordion
     * @fires accordion:resize
     */
    handleChangeView: function() {
      setTimeout(() => {
        maEventBus.$emit('accordion:resize')
      }, 600)
    },
    /**
     * @function
     * @name handleEvent
     * @description Wrapper that emits and event including if hasNestedComponents,
     *              and inputId, inputVal of the input that emit an event.
     * @param {String} action input, change... basically event method.
     * @param {Boolean} hasNestedComponents If there are nested components, form
     *                                      should be refreshed.
     * @param {Boolean} inputId Input id of transmitter event input.
     * @param {Boolean} inputVal Input value of transmitter event input.
     * @return {Object}
     */
    handleEvent: function(action, hasNestedComponents, inputId, inputVal) {
      this.$emit(action, {
        hasNestedComponents,
        inputId,
        inputVal
      })
    },
    /**
     * @function
     * @name handleEventTextNumber
     * @description Wrapper that emits and event including if hasNestedComponents,
     *              and inputId, inputVal of the input that emit an event.
     * @param {String} action input, change... basically event method.
     * @param {Boolean} hasNestedComponents If there are nested components, form
     *                                      should be refreshed.
     * @param {Boolean} inputId Input id of transmitter event input.
     * @param {Boolean} inputVal Input value of transmitter event input.
     * @param {Boolean} inpTextNumbers Input type
     * @param {Boolean} inpTextArea Input type

     * @return {Object}
     */
    handleEventTextNumber: function(
      action,
      hasNestedComponents,
      inputId,
      inputVal,
      inpTextNumbers,
      inpTextArea
    ) {
      this.$emit(action, {
        hasNestedComponents,
        inputId,
        inputVal,
        inpTextNumbers,
        inpTextArea
      })
    },

    getInput: function(form, inputType, name) {
      return form.inputs.find(
        input => input.type === inputType && input.name === name
      )
    },

    showNumberSelector: function(input) {
      if (input) {
        return input.name === PIECE
      }

      return false
    },

    onPieceSelected: function(pieceSelected) {
      if (pieceSelected) {
        this.updateRoomSurfaceInput(this.piecesSurfaceList, pieceSelected)
      }
    },

    getSelectedPiece: function() {
      if (
        this.lastPieceSelected &&
        this.lastPieceSelected.name &&
        this.lastPieceSelected.piece_id
      ) {
        return this.lastPieceSelected
      }

      return this.getPieceByDefault()
    },

    getPieceByDefault: function() {
      const piecesList = this.getPiecesList(this.data)
      if (piecesList) {
        return {
          name: piecesList.options[0].key,
          piece_id: piecesList.options[0].piece_id
        }
      }

      return null
    },

    getPiecesList: function(data) {
      return data.regions[0].inputs.find(formData => formData.name === PIECE)
    },

    processFormData: function(data, piecesMaxIdList) {
      const newList = []
      const piecesList = this.getPiecesList(data)

      if (piecesList) {
        piecesList.options.forEach(piece => {
          const pieceInfo = piecesMaxIdList.find(p => piece.key === p.piece)
          newList.push(this.mapPieceId(piece, pieceInfo))
        })

        piecesList.options = newList
      }

      return data
    },

    mapPieceId: function(piece, pieceInfo) {
      const defaultPieceValue = 1
      const maxId = pieceInfo
        ? parseInt(pieceInfo.max_id) + defaultPieceValue
        : 0
      return {
        ...piece,
        maxId,
        piece_id: defaultPieceValue
      }
    },

    updatePiecesSurface: function() {
      this.actUdatePiecesSurface(this.getPiecesSurfaceList())
      this.updateRoomSurfaceInput(
        this.piecesSurfaceList,
        this.getSelectedPiece()
      )
    },

    updateFormData: function() {
      this.data = this.processFormData(this.formData, this.piecesMaxIdList)
    },

    updateRoomSurfaceInput: function(piecesSurfaceList, pieceSelected) {
      const [form] = this.data.regions
      const surfaceInput = this.getInput(form, TEXT_NUMBERS, ROOM_SURFACE)
      const pieceData = piecesSurfaceList.find(piece => {
        return (
          piece.room === pieceSelected.name &&
          piece.piece_id === pieceSelected.piece_id
        )
      })

      if (surfaceInput) {
        if (
          pieceSelected &&
          pieceData &&
          this.lastPieceSelected.name !== 'editSearchBtn'
        ) {
          surfaceInput.selectedValue = String(pieceData.actual_surface || 0)
        } else {
          surfaceInput.selectedValue = String(0)
        }
      }
    },

    getPiecesSurfaceList: function() {
      const list = []
      this.sinisterPiecesList.forEach(piece => {
        const room = list.find(
          element =>
            element.room === piece.room && element.piece_id === piece.piece_id
        )
        if (!room) {
          list.push({
            room: piece.room,
            piece_id: piece.piece_id
            //actual_surface: piece.actual_surface
          })
        } else {
          room.actual_surface = piece.actual_surface
        }
      })

      return list
    },
    clearForm: function() {
      this.damage = {}
      this.form = {}
      this.updateFormData()
    }
  },

  beforeMount() {
    const input = this.getInput(form, TEXT_NUMBERS, ROOM_SURFACE)
    input.value = ''
  },

  mounted: function() {
    this.updateFormData()
    this.updatePiecesSurface()
    maEventbus.$on('view:change', () => {
      this.handleChangeView()
    })
    maEventBus.$on('update:area', data => {
      const [form] = this.data.regions
      const inputArea = this.getInput(form, TEXT_NUMBERS, DAMAGE_SURFACE)
      inputArea.selectedValue = String(data)
      this.handleEvent(
        'change',
        inputArea.hasNestedComponents,
        inputArea.name,
        inputArea.selectedValue,
        inputArea.inputVal
      )
    })

    maEventBus.$on(UPDATE_PIECES_IDS, () => {
      this.updateFormData()
    })
    maEventBus.$on(UPDATE_PIECES_SURFACE, () => {
      this.updatePiecesSurface()
    })
  },

  watch: {
    stateWarrantySinisterSummary: function(newSummary, oldSummary) {
      this.updateFormData()
      this.updatePiecesSurface()
    }
  }
}
